import React, { useCallback, useMemo } from "react";
import {
  Line, Bar, ReferenceLine, Area
} from 'recharts';
import { getChartColors, getImportantChartColors, renderCommonChart } from './commonChart';
import { formatDate } from "@/utils/format";
import { Item } from "@/api";

interface InventoryChartProps {
  items: Item[];
  inventories: Record<Item["品目ID"], number[]>;
  shipments: Record<Item["品目ID"], number[]>;
  productions: Record<Item["品目ID"], number[]>;
  日付: string[];
  compare?: boolean;
}

const InventoryChart: React.FC<InventoryChartProps> = ({
  items,
  inventories,
  shipments,
  productions,
  日付,
  compare = false,
}) => {
  const dates = 日付.map(d=>formatDate(d));
  console.log("render compare",compare)
  const chartData = useMemo(() => {
    return dates.map((date, index) => {
      const dataPoint: Record<string,string | number> = { date };
      items.forEach(item => {
        const itemId = item.品目ID;
        dataPoint[itemId] = inventories[itemId][index];
        dataPoint[`${itemId}_min`] =item.在庫下限.value
        dataPoint[`${itemId}_max`] = item.在庫上限.value
        dataPoint[`${itemId}_range`] = item.在庫上限.value - item.在庫下限.value
        dataPoint[`${itemId}_出荷`] = shipments[itemId][index];
        dataPoint[`${itemId}_生産`] = productions[itemId]?.[index];
        if(compare){
          dataPoint[`${itemId}_old`] = inventories[`${itemId}_old`][index];
          dataPoint[`${itemId}_old_生産`] = productions[`${itemId}_old`]?.[index];
        }
      });
      return dataPoint;
    });
  }, [items, inventories, shipments, productions, dates,compare]);

  const createChart = useCallback((item: Item, index: number) => {
    const itemId = item.品目ID;
    const itemColor = getChartColors(index);
    const oldItemColor = getChartColors(index+5);
    const shipColor = getImportantChartColors(index);
    const oldShipColor = getImportantChartColors(index+5);
    const hasInventoryLines = item.在庫上限 !== undefined && item.在庫下限 !== undefined;
    return [
    (
        <Line
          type="monotone"
          dataKey={itemId}
          stroke={itemColor}
          strokeWidth={2}
          yAxisId="left"
          dot={(props) => {
            const { value, cx, cy } = props;
            const min = item.在庫下限
            const max = item.在庫上限
            const isOutOfRange = value < min || value > max;
            const isMinus = value < 0;
            const color = isMinus ? 'red' : isOutOfRange ? 'orange' : itemColor;
            return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} />;
          }}
          name={`${item.品目名}の在庫`}
        />
    ),
    compare ? (
      <Line
        type="monotone"
        dataKey={`${itemId}_old`}
        stroke={oldItemColor}
        strokeWidth={1}
        yAxisId="left"
        name={`${item.品目名}の在庫(初期計画)`}
      />
  ) : null,
  (
      <Bar
      dataKey={`${itemId}_出荷`}
      fill={itemColor}
      fillOpacity={0.5}
      yAxisId="right"
      name={`${item.品目名}の出荷`}
      />
    ),(
      <Bar
      dataKey={`${itemId}_生産`}
      fill={shipColor}
      fillOpacity={1.0}
      yAxisId="right"
      name={`${item.品目名}の生産`}
      />
    ),
    compare ? (
      <Bar
      dataKey={`${itemId}_old_生産`}
      fill={oldShipColor}
      fillOpacity={1.0}
      yAxisId="right"
      name={`${item.品目名}の生産（初期計画)`}
      />
    ) : null].filter(Boolean)
  }, [compare])

  const charts = useMemo(() => {
    const ret=items.map(createChart).flat();
    if(items.length == 1){
      ret.push(
        <ReferenceLine y={items[0].在庫下限.value} stroke="red" strokeDasharray="3 3" label="下限" yAxisId="left" />
      )
      ret.push(
        <ReferenceLine y={items[0].在庫上限.value} stroke="green" strokeDasharray="3 3" label="上限" yAxisId="left" />
      )
    }
    return ret;
  }
  ,[createChart,items,compare]);

  return renderCommonChart(chartData, charts, items.length > 5, undefined, undefined, true) // 第6引数にtrueを追加して2軸を有効化
};

export default InventoryChart;
